import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/lib/seo'

const EFFECTIVE_DATE = 'May 27, 2026'
const CONTACT_EMAIL = 'info@cloudnineai.com'

export default function Privacy() {
  const { t } = useTranslation()
  useDocumentTitle(`${t('footer.privacy')} — ${t('site.name')}`)

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-8 py-24 prose prose-invert">
      <h1>{t('footer.privacy')}</h1>
      <p className="opacity-70">
        <strong>Effective date:</strong> {EFFECTIVE_DATE}
      </p>

      <p>
        This Privacy Policy describes how CloudnineAI (a Relavance Group company,
        &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;) collects, uses, and
        shares information when you visit this website or sign up for early access to ODE.
        It applies to this marketing site only. Use of the ODE product itself is covered by
        a separate agreement.
      </p>

      <h2>1. Who is responsible</h2>
      <p>
        CloudnineAI is the data controller for personal data collected through this site.
        Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>2. What we collect</h2>

      <h3>2.1 Information you give us</h3>
      <ul>
        <li>
          <strong>Early-access form:</strong> first name, last name, work email, company,
          role, intended use case, the types of data sources you work with, and your opt-in
          choice for product updates. Submitted via a Cloudflare Worker we operate.
        </li>
        <li>
          <strong>Newsletter sign-up:</strong> your email address.
        </li>
      </ul>

      <h3>2.2 Information collected automatically</h3>
      <ul>
        <li>
          <strong>Necessary technical data</strong> (no consent required): your IP address
          and basic request metadata, used briefly by Cloudflare to deliver the site, block
          abuse, and run the Turnstile bot-check on forms.
        </li>
        <li>
          <strong>Analytics data</strong> (only if you accept analytics in the consent
          banner): pages visited, referrer, device/browser type, approximate region derived
          from IP, and interactions you have with elements on the page (clicks, scroll
          milestones, form interactions). Collected through PostHog. We do <em>not</em>{' '}
          enable session recording, heatmaps, or surveys. We respect your browser&apos;s
          &quot;Do Not Track&quot; signal — if it&apos;s on, PostHog stays opted out even if
          you accept analytics.
        </li>
      </ul>

      <h3>2.3 What we do not collect</h3>
      <p>
        We do not run advertising, behavioural-ad, or cross-site tracking. We do not buy or
        enrich your contact information from data brokers. We do not knowingly collect
        information from children under 16.
      </p>

      <h2>3. Cookies and similar technologies</h2>
      <p>
        We use the following categories. You can change your choice at any time via the
        &quot;Privacy preferences&quot; link in the footer.
      </p>
      <ul>
        <li>
          <strong>Necessary</strong> — a local-storage entry that remembers your consent
          choice, and short-lived Cloudflare cookies used for Turnstile and request
          routing. Always on; cannot be disabled without breaking the site.
        </li>
        <li>
          <strong>Analytics</strong> — PostHog uses a first-party cookie and local-storage
          entry to assign a pseudonymous <code>distinct_id</code> so we can tell repeat
          visits apart from new ones. Set only after you accept. If you reject or have not
          chosen, PostHog runs in memory-only mode and writes nothing persistent.
        </li>
      </ul>

      <h2>4. Why we process this data (legal bases under GDPR)</h2>
      <ul>
        <li>
          <strong>Running the site, security, and fraud prevention</strong> — legitimate
          interest (Art. 6(1)(f) GDPR).
        </li>
        <li>
          <strong>Responding to your early-access or newsletter request</strong> — steps
          taken at your request prior to entering a contract (Art. 6(1)(b)), and your
          consent for marketing emails where required (Art. 6(1)(a)).
        </li>
        <li>
          <strong>Analytics</strong> — your consent (Art. 6(1)(a) GDPR; PECR / ePrivacy for
          UK and EU visitors). You can withdraw consent at any time without affecting prior
          processing.
        </li>
      </ul>

      <h2>5. Who we share data with</h2>
      <p>We use a small number of vendors that act as processors on our behalf:</p>
      <ul>
        <li>
          <strong>PostHog</strong> — product analytics. We use the EU instance
          (<code>eu.i.posthog.com</code>), so analytics data is stored in the European
          Union.
        </li>
        <li>
          <strong>Cloudflare</strong> — content delivery, DDoS protection, Turnstile
          bot-check, and the Worker that receives early-access submissions.
        </li>
        <li>
          <strong>Fly.io</strong> — hosting for this site.
        </li>
        <li>
          <strong>Email delivery provider</strong> — to send transactional and (with
          consent) update emails. [TODO: name the provider once selected.]
        </li>
      </ul>
      <p>
        We do not sell personal information, and we do not share it for cross-context
        behavioural advertising. We may disclose information if required by law, to enforce
        our terms, or to protect rights, property, or safety.
      </p>

      <h2>6. International data transfers</h2>
      <p>
        Some processors (e.g., Cloudflare) are based in the United States. Where personal
        data leaves the European Economic Area or the United Kingdom, we rely on the
        European Commission&apos;s Standard Contractual Clauses, the UK International Data
        Transfer Addendum, and supplementary measures as required.
      </p>

      <h2>7. How long we keep it</h2>
      <ul>
        <li>
          <strong>Early-access submissions:</strong> for as long as your request is active,
          then up to 24 months for follow-up, after which we delete or anonymise.
        </li>
        <li>
          <strong>Newsletter:</strong> until you unsubscribe.
        </li>
        <li>
          <strong>Analytics events:</strong> retained by PostHog according to our project
          settings, currently 12 months.
        </li>
        <li>
          <strong>Server logs:</strong> up to 30 days for security and debugging.
        </li>
      </ul>

      <h2>8. Your rights</h2>
      <p>
        Depending on where you are, you may have the right to access, correct, delete,
        restrict, or object to our processing of your personal data, to receive a portable
        copy, and to withdraw consent. EU/UK residents also have the right to lodge a
        complaint with their local supervisory authority.
      </p>
      <p>
        California residents have the right to know, delete, correct, and limit the use of
        sensitive personal information, and the right to not be discriminated against for
        exercising these rights. We do not sell or share personal information as those
        terms are defined under the CCPA/CPRA.
      </p>
      <p>
        To exercise any of these rights, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We&apos;ll respond within
        the timeframes required by applicable law.
      </p>

      <h2>9. Security</h2>
      <p>
        We use HTTPS everywhere, scope access to personal data to people who need it, and
        rely on our vendors&apos; security controls. No method of transmission or storage
        is 100% secure.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We&apos;ll post any updates here and revise the effective date above. For material
        changes affecting how we process your personal data, we&apos;ll provide additional
        notice (e.g., by email or a banner on the site).
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        <br />
        [TODO: add postal address and, if required, EU/UK Article 27 representative
        details.]
      </p>
    </section>
  )
}
